import type { ComponentProps, ReactNode } from "react";

import { Menu as AntMenu } from "antd";
import type { MenuItemGroupProps, MenuProps } from "antd/es/menu";
import type { MenuItemProps } from "antd/es/menu/MenuItem";
import type { SubMenuProps } from "antd/es/menu/SubMenu";

import { cn } from "@btnet/utilities-core";

import "./BuiMenu.less";

/**
 * BuiMenuItem props - extends antd MenuItemProps with testid.
 * Provides full access to antd's Menu.Item API.
 *
 * Note: `children` is explicitly included due to React 18 type changes
 * (antd 4.x types were built for React 17 where FC implicitly included children).
 */
export interface IBuiMenuItemProps extends MenuItemProps {
  testid: string;
  children?: ReactNode;
}

/**
 * BuiMenu props - extends antd MenuProps with testid.
 * Provides full access to antd's Menu API.
 *
 * Note: `children` is explicitly included due to React 18 type changes
 * (antd 4.x types were built for React 17 where FC implicitly included children).
 */
export interface IBuiMenuProps extends MenuProps {
  testid: string;
  children?: ReactNode;
}

export interface IBuiSubMenuProps extends SubMenuProps {
  testid: string;
  children?: ReactNode;

  /**
   * rc-menu's identity for this node — it drives open/active state, so siblings
   * must not share one. antd normally derives it from the React `key`, but that
   * only happens for direct children of a `Menu`; a submenu rendered through a
   * wrapper component has its `key` consumed by React and never reaches rc-menu,
   * leaving every sibling with the same id. Pass it explicitly in that case.
   */
  eventKey?: string;
}

/**
 * BuiMenuDivider props - antd Menu.Divider's props (e.g. `dashed`, `className`).
 */
export type IBuiMenuDividerProps = ComponentProps<typeof AntMenu.Divider>;

/**
 * BuiMenuItemGroup props - a titled section of menu items.
 */
export interface IBuiMenuItemGroupProps extends MenuItemGroupProps {
  testid: string;
  children?: ReactNode;
}

/**
 * BuiMenuItem sub-component for individual menu items.
 * A styled wrapper around antd Menu.Item with full access to its API.
 * @param root0 - The component props.
 * @param root0.testid - Test ID for the menu item.
 * @param root0.className - Additional CSS class names.
 */
const BuiMenuItem = ({ testid, className, ...props }: IBuiMenuItemProps) => (
  <AntMenu.Item
    data-testid={testid}
    className={cn("bui-menu-item", className)}
    {...props}
  />
);

/**
 * BuiSubMenu sub-component for individual sub-menu items.
 * A styled wrapper around antd Menu.SubMenu with full access to its API.
 * @param root0 - The component props.
 * @param root0.testid - Test ID for the sub-menu item.
 * @param root0.className - Additional CSS class names.
 * @param root0.popupClassName - Additional CSS class names for the popup submenu.
 * @example
 * ```tsx
 *<BuiMenu testid="nav-menu">
 *  <BuiMenu.Item testid="plans">
 *    <a href="/plans">Plans</a>
 *  </BuiMenu.Item>
 *  <BuiSubMenu testid="resources" title="Resources">
 *    <BuiMenu.Item testid="docs">
 *      <a href="/docs">Documents</a>
 *    </BuiMenu.Item>
 *  </BuiSubMenu>
 * </BuiMenu>
 * ```
 */
const BuiSubMenu = ({
  testid,
  className,
  popupClassName,
  ...props
}: IBuiSubMenuProps) => (
  <AntMenu.SubMenu
    {...props}
    data-testid={testid}
    className={cn("bui-submenu", className)}
    popupClassName={cn("bui-submenu-popup", popupClassName)}
  />
);

/**
 * BuiMenuDivider sub-component for visual separation between menu items.
 * A styled wrapper around antd Menu.Divider with full access to its API.
 * @param root0 - The component props.
 * @param root0.className - Additional CSS class names.
 */
const BuiMenuDivider = ({ className, ...props }: IBuiMenuDividerProps) => (
  <AntMenu.Divider className={cn("bui-menu-divider", className)} {...props} />
);

/**
 * BuiMenuItemGroup sub-component for a titled section of menu items.
 * A styled wrapper around antd Menu.ItemGroup. The title renders as a
 * non-interactive filled header band above the group's items - it separates
 * sections by its own fill, so a group replaces a `BuiMenu.Divider` rather
 * than sitting next to one.
 * @param root0 - The component props.
 * @param root0.testid - Test ID for the group.
 * @param root0.className - Additional CSS class names.
 * @example
 * ```tsx
 * <BuiMenu testid="actions-menu">
 *   <BuiMenu.ItemGroup testid="view-group" title="View">
 *     <BuiMenu.Item testid="open">Open in new tab</BuiMenu.Item>
 *   </BuiMenu.ItemGroup>
 * </BuiMenu>
 * ```
 */
const BuiMenuItemGroup = ({
  testid,
  className,
  ...props
}: IBuiMenuItemGroupProps) => (
  <AntMenu.ItemGroup
    {...props}
    data-testid={testid}
    className={cn("bui-menu-item-group", className)}
  />
);

/**
 * BuiMenu component - a styled wrapper around antd Menu.
 * Provides full access to antd's Menu API with Blueprint styling.
 * Intended for use as dropdown content with antd Dropdown or BTDropdown.
 * @param root0 - The component props.
 * @param root0.testid - Test ID for the menu.
 * @param root0.className - Additional CSS class names.
 * @example
 * ```tsx
 * import { Dropdown } from "antd";
 *
 * <Dropdown
 *   overlay={
 *     <BuiMenu testid="nav-menu">
 *       <BuiMenu.Item testid="plans">
 *         <a href="/plans">Plans</a>
 *       </BuiMenu.Item>
 *       <BuiMenu.Item testid="docs">
 *         <a href="/docs">Documents</a>
 *       </BuiMenu.Item>
 *     </BuiMenu>
 *   }
 *   trigger={["click"]}
 * >
 *   <button aria-haspopup="true">...</button>
 * </Dropdown>
 * ```
 */
const BuiMenuComponent = ({ testid, className, ...props }: IBuiMenuProps) => (
  <AntMenu
    data-testid={testid}
    className={cn("bui-menu", className)}
    {...props}
  />
);

/**
 * BuiMenu component - a styled wrapper around antd Menu.
 * Provides full access to antd's Menu API with Blueprint styling.
 * Intended for use as dropdown content with antd Dropdown or BTDropdown.
 * @example
 * ```tsx
 * import { Dropdown } from "antd";
 *
 * <Dropdown
 *   overlay={
 *     <BuiMenu testid="nav-menu">
 *       <BuiMenu.Item testid="plans">
 *         <a href="/plans">Plans</a>
 *       </BuiMenu.Item>
 *       <BuiMenu.Item testid="docs">
 *         <a href="/docs">Documents</a>
 *       </BuiMenu.Item>
 *     </BuiMenu>
 *   }
 *   trigger={["click"]}
 * >
 *   <button aria-haspopup="true">...</button>
 * </Dropdown>
 * ```
 */
export const BuiMenu = Object.assign(BuiMenuComponent, {
  /**
   * BuiMenuItem sub-component for individual menu items.
   * A styled wrapper around antd Menu.Item with full access to its API.
   */
  Item: BuiMenuItem,
  /**
   * BuiSubMenu sub-component for individual sub-menu items.
   * A styled wrapper around antd Menu.SubMenu with full access to its API.
   * @example
   * ```tsx
   *<BuiMenu testid="nav-menu">
   *  <BuiMenu.Item testid="plans">
   *    <a href="/plans">Plans</a>
   *  </BuiMenu.Item>
   *  <BuiSubMenu testid="resources" title="Resources">
   *    <BuiMenu.Item testid="docs">
   *      <a href="/docs">Documents</a>
   *    </BuiMenu.Item>
   *  </BuiSubMenu>
   * </BuiMenu>
   * ```
   */
  SubMenu: BuiSubMenu,
  /**
   * BuiMenuDivider sub-component for visual separation between menu items.
   * A styled wrapper around antd Menu.Divider with full access to its API.
   */
  Divider: BuiMenuDivider,
  /**
   * BuiMenuItemGroup sub-component for a titled section of menu items.
   * A styled wrapper around antd Menu.ItemGroup.
   */
  ItemGroup: BuiMenuItemGroup,
});
